import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortfolioPage() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState("Toate");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/portfolio`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []);

  const categories = useMemo(() => {
    if (!items) return [];
    return ["Toate", ...new Set(items.map((i) => i.category))];
  }, [items]);

  const filtered = !items ? [] : active === "Toate" ? items : items.filter((i) => i.category === active);

  return (
    <div data-testid="portfolio-page">
      <SEO
        metaKey="portofoliu"
        title="Portofoliu — Lucrări Metalice Finalizate | HEAVY METAL"
        description="Porți, garduri, terase, structuri și mobilier metalic — proiecte reale executate în atelierul HEAVY METAL din Buzău, pentru clienți din toată România."
        image="/images/porti.jpg"
      />
      <PageHero
        kicker={t("nav_portfolio")}
        title={t("pf_title")}
        subtitle={t("pf_sub")}
        image="/images/porti.jpg"
      />

      <section>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          {items && items.length > 0 && (
            <div className="flex flex-wrap gap-2.5" data-testid="portfolio-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  data-testid={`portfolio-filter-${c}`}
                  className={`border px-4 py-2 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-300 ${
                    active === c
                      ? "border-ink bg-ink text-base"
                      : "border-ink/15 text-ink/60 hover:border-ink hover:text-ink"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {items === null ? (
            <p className="kicker mt-10">{t("pf_loading")}</p>
          ) : filtered.length === 0 ? (
            <div className="mt-10 border border-ink/10 bg-surface p-16 text-center" data-testid="portfolio-empty-state">
              <p className="font-heading text-2xl font-semibold">{t("pf_empty_t")}</p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-ink/55">
                {t("pf_empty_x")}
              </p>
            </div>
          ) : (
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item, i) => (
                <Reveal key={item.id} delay={(i % 3) * 0.08}>
                  <div
                    className="group cursor-zoom-in border border-ink/10 bg-surface"
                    data-testid={`portfolio-item-${item.id}`}
                    onClick={() => setSelected(item)}
                  >
                    <div className="h-64 overflow-hidden">
                      <img
                        src={`${API}/files/${item.storage_path}`}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">{item.category}</p>
                      <h3 className="mt-2 font-heading text-xl font-semibold">{item.title}</h3>
                      {item.location && (
                        <p className="mt-2 flex items-center gap-1.5 text-sm text-ink/55">
                          <MapPin className="h-3.5 w-3.5 text-gold" /> {item.location}
                        </p>
                      )}
                      {item.description && (
                        <p className="mt-3 text-sm leading-relaxed text-ink/55">{item.description}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/92 p-4 backdrop-blur-sm sm:p-10"
            onClick={() => setSelected(null)}
            data-testid="portfolio-lightbox"
          >
            <button
              onClick={() => setSelected(null)}
              aria-label="Închide"
              data-testid="portfolio-lightbox-close"
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/40 text-white transition-colors hover:bg-white hover:text-black"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={`${API}/files/${selected.storage_path}`}
                alt={selected.title}
                className="mx-auto max-h-[76vh] w-auto max-w-full object-contain shadow-2xl"
              />
              <figcaption className="mt-4 flex flex-wrap items-baseline justify-between gap-2 text-white">
                <span className="font-heading text-xl font-semibold sm:text-2xl">{selected.title}</span>
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/55">
                  {selected.category}{selected.location ? ` — ${selected.location}` : ""}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
